/* [EZ] Bottom Sheet — mobile-only (<=960px). Turns native <select> dropdowns and the
   .ez-search__facet filter dropdowns into an iOS-style sheet that slides up from the bottom.
   Uniform across the site; navigation menus are custom (not <select>/facets) so they're untouched.
   Opt out any control with data-no-sheet. Progressive enhancement — desktop keeps native dropdowns. */
(function () {
  'use strict';
  var MQ = window.matchMedia('(max-width: 960px)');
  // Anti-misclick: after the sheet opens, ignore any tap until the slide-up settles, so the
  // opening tap can't ghost-select the option/link now sitting under the finger (the Sort
  // "one tap opens AND selects" bug). Tied to the ~0.28s open animation.
  var GUARD = 400;
  var sheet, backdrop, titleEl, bodyEl, doneBtn, current = null, openedAt = 0;
  function guarded() { return Date.now() - openedAt < GUARD; }

  function build() {
    backdrop = document.createElement('div');
    backdrop.className = 'ez-sheet__backdrop';
    sheet = document.createElement('div');
    sheet.className = 'ez-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.innerHTML =
      '<div class="ez-sheet__handle" aria-hidden="true"></div>' +
      '<div class="ez-sheet__head"><span class="ez-sheet__title"></span>' +
      '<button type="button" class="ez-sheet__done">Done</button></div>' +
      '<div class="ez-sheet__body"></div>';
    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);
    titleEl = sheet.querySelector('.ez-sheet__title');
    bodyEl = sheet.querySelector('.ez-sheet__body');
    doneBtn = sheet.querySelector('.ez-sheet__done');
    // Ignore the trailing click from the opening tap (it lands on the just-shown backdrop,
    // which would otherwise close the sheet instantly — the "shows then vanishes" bug).
    backdrop.addEventListener('click', function () { if (guarded()) return; close(); });
    doneBtn.addEventListener('click', close);
  }

  function open() {
    openedAt = Date.now();
    document.body.classList.add('ez-sheet-open');
    backdrop.classList.add('is-open');
    sheet.classList.add('is-open');
  }
  function close() {
    if (!current) return;
    sheet.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    document.body.classList.remove('ez-sheet-open');
    var onClose = current.onClose;
    current = null;
    if (onClose) onClose();
  }

  function labelFor(sel) {
    if (sel.id) { var l = document.querySelector('label[for="' + sel.id + '"]'); if (l) return l.textContent.trim(); }
    var p = sel.closest('label');
    return p ? p.textContent.trim() : '';
  }

  /* Single-select <select>: pick an option → set value + fire change (so existing handlers,
     e.g. the filter/sort auto-submit, still run). Closes on pick. */
  function openSelect(sel) {
    if (current) return;
    titleEl.textContent = sel.getAttribute('data-sheet-title') || labelFor(sel) || 'Select';
    doneBtn.style.display = 'none';
    bodyEl.innerHTML = '';
    Array.prototype.forEach.call(sel.options, function (opt) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'ez-sheet__opt' + (opt.selected ? ' is-selected' : '') + (opt.disabled ? ' is-disabled' : '');
      row.textContent = opt.textContent;
      if (opt.disabled) row.disabled = true;
      row.addEventListener('click', function () {
        if (guarded()) return;
        sel.value = opt.value;
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        close();
      });
      bodyEl.appendChild(row);
    });
    current = { onClose: null };
    open();
  }

  /* Filter facet (<details> with checkboxes or a price range): toggles are batched onto the
     real in-form inputs (no per-toggle reload); dismissing the sheet applies (submits once). */
  function openFacet(facet) {
    if (current) return;
    var summary = facet.querySelector('summary');
    titleEl.textContent = (summary && summary.firstChild ? summary.firstChild.textContent : 'Filter').trim() || 'Filter';
    doneBtn.style.display = '';
    doneBtn.textContent = 'Apply';
    bodyEl.innerHTML = '';
    var form = facet.closest('form');
    var price = facet.querySelector('.ez-search__facet-menu--price');
    // Only reload on dismiss if the user actually changed something — opening a facet and tapping
    // outside without picking anything must NOT trigger a refresh.
    var dirty = false;

    if (price) {
      var wrap = document.createElement('div');
      wrap.className = 'ez-sheet__price';
      Array.prototype.forEach.call(price.querySelectorAll('input[type="number"]'), function (real) {
        var proxy = document.createElement('input');
        proxy.type = 'number';
        proxy.inputMode = 'decimal';
        proxy.placeholder = real.placeholder || '';
        proxy.value = real.value;
        proxy.addEventListener('input', function () { real.value = proxy.value; dirty = true; });
        wrap.appendChild(proxy);
      });
      bodyEl.appendChild(wrap);
    } else {
      Array.prototype.forEach.call(
        facet.querySelectorAll('.ez-search__facet-menu input[type="checkbox"], .ez-search__facet-menu input[type="radio"]'),
        function (real) {
          var lbl = real.closest('label');
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'ez-sheet__opt' + (real.checked ? ' is-selected' : '') + (real.disabled ? ' is-disabled' : '');
          row.textContent = (lbl ? lbl.textContent : real.value).trim();
          if (real.disabled) row.disabled = true;
          row.addEventListener('click', function () {
            if (guarded()) return;
            real.checked = !real.checked; // set property only — no per-toggle submit
            row.classList.toggle('is-selected', real.checked);
            dirty = true;
          });
          bodyEl.appendChild(row);
        }
      );
    }
    // Apply on dismissal (Done or backdrop) ONLY if something changed — otherwise no reload.
    current = { onClose: function () { if (dirty && form) { form.requestSubmit ? form.requestSubmit() : form.submit(); } } };
    open();
  }

  /* Link-list mode: a set of <a> links (a custom nav dropdown) become sheet rows. Tapping a row
     re-dispatches a click on the real <a> so its existing handlers (scrollspy scroll, tab switch)
     run unchanged. Keeps the custom collection-nav / product-nav menus uniform with the selects. */
  function openLinks(links, title) {
    if (current) return;
    titleEl.textContent = title || 'Menu';
    doneBtn.style.display = 'none';
    bodyEl.innerHTML = '';
    Array.prototype.forEach.call(links, function (a) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'ez-sheet__opt' + (a.classList.contains('is-active') ? ' is-selected' : '');
      row.textContent = a.textContent.trim();
      row.addEventListener('click', function () {
        if (guarded()) return;
        close();
        a.click(); // run the link's real handlers (smooth-scroll / tab switch / navigate)
      });
      bodyEl.appendChild(row);
    });
    current = { onClose: null };
    open();
  }

  // Custom link-menu triggers to route through the sheet (kept in sync with the inline menus,
  // which stay the desktop UI). Intercepted in the capture phase so the inline-toggle handler
  // on the same button never runs on mobile.
  var LINK_TRIGGERS = [
    { trigger: '[data-collindex-trigger]', wrap: '[data-collindex-navwrap]', links: 'ul a', title: 'Collections' },
    { trigger: '[data-pnav-trigger]', wrap: '[data-pnav-anchorwrap]', links: '.ez-pnav__anchors a', title: 'Sections' }
  ];

  var reWire;
  function wire() {
    if (!MQ.matches) return;
    if (!sheet) build();
    // Re-wire when new dropdowns appear after first paint (e.g. Tabbed Products mirrors its tabs
    // into a <select> at init, after this ran once). wire() is idempotent via data-sheet-wired.
    if (!reWire) { reWire = true; new MutationObserver(function () { clearTimeout(wire._t); wire._t = setTimeout(wire, 150); }).observe(document.body, { childList: true, subtree: true }); }

    LINK_TRIGGERS.forEach(function (cfg) {
      document.querySelectorAll(cfg.trigger + ':not([data-sheet-wired])').forEach(function (trg) {
        trg.setAttribute('data-sheet-wired', '');
        trg.addEventListener('click', function (e) {
          if (!MQ.matches) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          var wrap = trg.closest(cfg.wrap) || document;
          var links = wrap.querySelectorAll(cfg.links);
          if (links.length) openLinks(links, cfg.title);
        }, true);
      });
    });

    document.querySelectorAll('select:not([data-no-sheet]):not([data-sheet-wired])').forEach(function (sel) {
      sel.setAttribute('data-sheet-wired', '');
      var intercept = function (e) { if (MQ.matches) { e.preventDefault(); if (sel.blur) sel.blur(); openSelect(sel); } };
      sel.addEventListener('pointerdown', intercept);
      sel.addEventListener('mousedown', intercept);
      sel.addEventListener('keydown', function (e) {
        if (MQ.matches && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) { e.preventDefault(); openSelect(sel); }
      });
    });

    document.querySelectorAll('.ez-search__facet:not([data-sheet-wired])').forEach(function (facet) {
      facet.setAttribute('data-sheet-wired', '');
      var summary = facet.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (e) {
        if (!MQ.matches) return;
        e.preventDefault();
        facet.removeAttribute('open'); // never open the inline menu on mobile
        openFacet(facet);
      });
    });
  }

  if (document.readyState !== 'loading') wire();
  else document.addEventListener('DOMContentLoaded', wire);
  document.addEventListener('shopify:section:load', wire);
})();
